import { createContext, useContext, useLayoutEffect, useMemo, useRef } from "react";
import { registerPanel, registerLayer } from "./panelRegistry";

/* Lets Parallax find the Panel it lives in without prop drilling. */
export const PanelContext = createContext(null);

/* ── Panel ────────────────────────────────────────────────
   One viewport-sized slide of the horizontal journey. The
   outer div is the measured, clipped slot; the inner div is
   what the registry dims/lifts as the slide moves off-center
   (transform + opacity only, written from cached offsets).
   In vertical (mobile) mode it degrades to a normal block. */
export function Panel({ horizontal, children, className = "", width = 1 }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const rec = useMemo(() => ({ el: null, inner: null, layers: new Set() }), []);

  useLayoutEffect(() => {
    if (!horizontal) return;
    rec.el = outerRef.current;
    rec.inner = innerRef.current;
    return registerPanel(rec);
  }, [horizontal, rec]);

  if (!horizontal) {
    return <div className={`relative ${className}`}>{children}</div>;
  }

  return (
    <PanelContext.Provider value={rec}>
      <div
        ref={outerRef}
        className="relative h-full shrink-0 overflow-hidden"
        style={{ width: `${width * 100}vw` }}
      >
        <div ref={innerRef} className={`relative h-full w-full ${className}`}>
          {children}
        </div>
      </div>
    </PanelContext.Provider>
  );
}

/* ── Parallax ─────────────────────────────────────────────
   DOM depth layer: shifts its child against the camera as the
   panel crosses the viewport. depth > 0 lags behind (far),
   depth < 0 leads (near). maxShift caps the travel so text
   can't slide past the panel's padding — raise it only for
   decorative layers (watermarks) where cropping is the look. */
export function Parallax({ horizontal, depth = 0.08, maxShift = 24, className = "", children }) {
  const panel = useContext(PanelContext);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    if (!horizontal || !panel || !innerRef.current) return;
    return registerLayer(panel, { node: innerRef.current, depth, maxShift });
  }, [horizontal, panel, depth, maxShift]);

  return (
    <div className={className}>
      <div ref={innerRef} className="will-change-transform">
        {children}
      </div>
    </div>
  );
}
